import {
  Button,
  Card,
  Dialog,
  DialogPanel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextInput,
} from "@tremor/react";
import {
  Bell,
  Mail,
  MessageCircle,
  Phone,
  PhoneCall,
  Plus,
  Trash2,
  UserMinus,
  Webhook,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
// Mutation calls still go through the api/* functions directly — the nested
// editor / endpoint / member panels each touch the server and the parent then
// invalidates the channels list via the query client below. This keeps
// the wiring minimal without sacrificing cross-page cache freshness.
import {
  addChannelEndpoint,
  addChannelMember,
  createChannel,
  deleteChannel,
  getChannel,
  removeChannelEndpoint,
  removeChannelMember,
  testChannel,
  updateChannel,
} from "../api/channels.js";
import { useQueryClient } from "@tanstack/react-query";
import { useChannels } from "../queries/channels.js";
import { useMembers } from "../queries/workspaces.js";
import { usePlan } from "../lib/usePlan.js";
import { canUseChannel } from "../lib/planGate.js";
import UpgradePrompt from "../components/UpgradePrompt.jsx";
import { keys } from "../queryKeys.js";

import { useTranslation } from "react-i18next";

const CHANNEL_TYPES = [
  "email",
  "slack",
  "discord",
  "telegram",
  "bitrix24",
  "webhook",
  "sms",
  "voice",
  "msteams",
  "pagerduty",
  "opsgenie",
];

const CHANNEL_META = {
  email: {
    labelKey: "endpoints.types.email",
    Icon: Mail,
    configField: "email",
    placeholder: "ops@example.com",
  },
  slack: {
    labelKey: "endpoints.types.slack",
    Icon: Bell,
    configField: "webhook_url",
    placeholder: "https://hooks.slack.com/services/...",
  },
  discord: {
    labelKey: "endpoints.types.discord",
    Icon: MessageCircle,
    configField: "webhook_url",
    placeholder: "https://discord.com/api/webhooks/...",
  },
  telegram: {
    labelKey: "endpoints.types.telegram",
    Icon: MessageCircle,
    configField: "chat_id",
    placeholder: "-1001234567890",
  },
  webhook: {
    labelKey: "endpoints.types.webhook",
    Icon: Webhook,
    configField: "url",
    placeholder: "https://example.com/hooks/monitero",
  },
  sms: {
    labelKey: "endpoints.types.sms",
    Icon: Phone,
    configField: "to_number",
    placeholder: "+14155552671",
  },
  voice: {
    labelKey: "endpoints.types.voice",
    Icon: PhoneCall,
    configField: "to_number",
    placeholder: "+14155552671",
  },
  msteams: {
    labelKey: "endpoints.types.msteams",
    Icon: Webhook,
    configField: "webhook_url",
    placeholder: "https://...webhook.office.com/... or Power Automate URL",
  },
  pagerduty: {
    labelKey: "endpoints.types.pagerduty",
    Icon: Bell,
    configField: "routing_key",
    placeholder: "Events API v2 integration key",
  },
  opsgenie: {
    labelKey: "endpoints.types.opsgenie",
    Icon: Bell,
    configField: "api_key",
    placeholder: "Opsgenie API key",
    regionSelect: true,
  },
  bitrix24: {
    labelKey: "endpoints.types.bitrix24",
    Icon: MessageCircle,
    configField: "webhook_url",
    placeholder: "https://company.bitrix24.com/rest/1/token/",
    secondConfigField: "dialog_id",
    secondPlaceholder: "chat123  or user ID",
  },
};

function endpointLabel(endpoint, t) {
  const meta = CHANNEL_META[endpoint.type];
  const value = endpoint.config[meta?.configField] || "";
  const display = endpoint.config.label || value;
  const typeLabel = t(meta?.labelKey ?? `endpoints.types.${endpoint.type}`);
  return display ? `${typeLabel} - ${display}` : typeLabel;
}

function EndpointsSection({ channelId, endpoints, onEndpointAdded, onEndpointRemoved }) {
  const { t } = useTranslation("channels");
  const plan = usePlan();
  const [type, setType] = useState(() =>
    canUseChannel("slack", plan) ? "slack" : "email"
  );
  const [configValue, setConfigValue] = useState("");
  const [label, setLabel] = useState("");
  const [region, setRegion] = useState("us");
  const [saving, setSaving] = useState(false);

  const [secondConfigValue, setSecondConfigValue] = useState("");
  const meta = CHANNEL_META[type] ?? CHANNEL_META.email;
  const typeLocked = !canUseChannel(type, plan);

  async function handleAdd() {
    if (!configValue.trim()) return;
    if (meta.secondConfigField && !secondConfigValue.trim()) return;

    const endpointData = {
      type,
      config: { [meta.configField]: configValue.trim() },
    };
    if (meta.secondConfigField) endpointData.config[meta.secondConfigField] = secondConfigValue.trim();
    if (label.trim()) endpointData.config.label = label.trim();
    if (meta.regionSelect) endpointData.config.region = region;

    if (channelId) {
      setSaving(true);
      try {
        const endpoint = await addChannelEndpoint(channelId, endpointData);
        onEndpointAdded(endpoint);
        setConfigValue("");
        setSecondConfigValue("");
        setLabel("");
        toast.success(t("endpoints.success_add"));
      } catch (err) {
        toast.error(err.response?.data?.error ?? "Failed to add endpoint");
      } finally {
        setSaving(false);
      }
    } else {
      // Local stage mode (for new channel creation)
      onEndpointAdded({
        ...endpointData,
        id: `local-${Date.now()}`, // Temporary ID for list rendering
      });
      setConfigValue("");
      setSecondConfigValue("");
      setLabel("");
    }
  }

  async function handleRemove(endpointId) {
    if (channelId) {
      try {
        await removeChannelEndpoint(channelId, endpointId);
        onEndpointRemoved(endpointId);
        toast.success(t("endpoints.success_remove"));
      } catch (err) {
        toast.error(err.response?.data?.error ?? "Failed to remove endpoint");
      }
    } else {
      onEndpointRemoved(endpointId);
    }
  }

  return (
    <div>
      <label className="mb-2 block text-xs font-medium text-gray-600">
        {t("endpoints.title")}
      </label>
      <p className="mb-3 text-xs text-gray-400">{t("endpoints.description")}</p>

      {endpoints.length > 0 && (
        <div className="mb-3 divide-y divide-gray-100 rounded-md border border-gray-200">
          {endpoints.map((e) => {
            const Icon = CHANNEL_META[e.type]?.Icon ?? Bell;
            return (
              <div key={e.id} className="flex items-center justify-between px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="text-sm text-gray-700">{endpointLabel(e, t)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(e.id)}
                  className="rounded p-1 text-gray-400 hover:text-red-500"
                  title={t("endpoints.remove")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex gap-2">
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setConfigValue("");
              setSecondConfigValue("");
            }}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
          >
            {CHANNEL_TYPES.map((tkey) => {
              const locked = !canUseChannel(tkey, plan);
              return (
                <option key={tkey} value={tkey} disabled={locked}>
                  {t(CHANNEL_META[tkey].labelKey)}
                  {locked ? " 🔒" : ""}
                </option>
              );
            })}
          </select>
          <input
            type="text"
            value={configValue}
            onChange={(e) => setConfigValue(e.target.value)}
            placeholder={meta.placeholder}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          {meta.regionSelect && (
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-600"
              title="Opsgenie region"
            >
              <option value="us">US</option>
              <option value="eu">EU</option>
            </select>
          )}
        </div>
        {meta.secondConfigField && (
          <input
            type="text"
            value={secondConfigValue}
            onChange={(e) => setSecondConfigValue(e.target.value)}
            placeholder={meta.secondPlaceholder}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-teal-600"
          />
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("endpoints.label_optional")}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-teal-600"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={saving || typeLocked || !configValue.trim() || (meta.secondConfigField && !secondConfigValue.trim())}
            icon={Plus}
            onClick={handleAdd}
          >
            {t("endpoints.add")}
          </Button>
        </div>
        {typeLocked && <UpgradePrompt feature={`${type}_alerts`} />}
      </div>
    </div>
  );
}

function prefSummary(prefs, t) {
  if (!prefs) return t("prefs.email");
  const parts = [];
  if (prefs.email_enabled !== false) parts.push(t("prefs.email"));
  if (prefs.slack_dm_enabled) parts.push(t("prefs.slack"));
  if (prefs.phone_number) parts.push(t("prefs.phone"));
  return parts.length > 0 ? parts.join(" · ") : t("prefs.none");
}

// ---------------------------------------------------------------------------
// Delivery log
// ---------------------------------------------------------------------------

function DeliveryLogSection({ channelId }) {
  const [deliveries, setDeliveries] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDeliveries(null);
    import("../api/channels.js")
      .then((m) => m.fetchChannelDeliveries(channelId, 20))
      .then(setDeliveries)
      .catch(() => setDeliveries([]));
  }, [channelId, open]);

  function relTime(iso) {
    const secs = (Date.now() - new Date(iso).getTime()) / 1000;
    if (secs < 90) return "just now";
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <button
        type="button"
        className="flex w-full items-center justify-between text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Recent deliveries
        </span>
        <span className="text-xs text-gray-400">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="mt-2">
          {deliveries === null ? (
            <p className="text-xs text-gray-400">Loading…</p>
          ) : deliveries.length === 0 ? (
            <p className="text-xs text-gray-400">No delivery history yet.</p>
          ) : (
            <ul className="space-y-1">
              {deliveries.map((d) => (
                <li key={d.id} className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs ${d.status === "success" ? "bg-green-50" : "bg-red-50"}`}>
                  <span className={`mt-0.5 shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold uppercase ${d.status === "success" ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}>
                    {d.status}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-gray-700">
                      <span className="font-medium capitalize">{d.channel_type}</span>
                      {" · "}
                      <span className="text-gray-500">{d.notification_type}</span>
                    </p>
                    {d.error_message && (
                      <p className="mt-0.5 truncate text-red-600" title={d.error_message}>
                        {d.error_message}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-gray-400">{relTime(d.attempted_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / edit panel
// ---------------------------------------------------------------------------

function ChannelPanel({ channel, allMembers, onClose, onSaved }) {
  const { t } = useTranslation("channels");
  const isEdit = Boolean(channel);

  const [name, setName] = useState(channel?.name ?? "");
  const [description, setDescription] = useState(channel?.description ?? "");
  // For create: local list of members to add after creation.
  // For edit: mirrors the saved member list; mutations go straight to the API.
  const [members, setMembers] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(isEdit);
  const [addingUserId, setAddingUserId] = useState("");
  const [saving, setSaving] = useState(false);
  const [memberSaving, setMemberSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoadingMembers(true);
    getChannel(channel.id)
      .then((full) => {
        setMembers(full.members ?? []);
        setEndpoints(full.endpoints ?? []);
      })
      .catch(() => toast.error("Could not load channel members"))
      .finally(() => setLoadingMembers(false));
  }, [channel?.id]);

  const memberIds = new Set(members.map((m) => m.user_id ?? m.id));
  const nonMembers = allMembers.filter((m) => !memberIds.has(m.user_id ?? m.id));

  // ---------------------------------------------------------------------------
  // For edit mode: add/remove members immediately via API
  // For create mode: manage the list locally; sync after channel is created
  // ---------------------------------------------------------------------------

  async function handleAddMember() {
    if (!addingUserId) return;
    const user = allMembers.find((m) => (m.user_id ?? m.id) === addingUserId);
    if (!user) return;

    if (isEdit) {
      setMemberSaving(true);
      try {
        await addChannelMember(channel.id, addingUserId);
        setMembers((prev) => [
          ...prev,
          {
            user_id: addingUserId,
            email: user.email,
            full_name: user.full_name,
            notification_prefs: null,
          },
        ]);
        setAddingUserId("");
        toast.success(t("panel.success_add"));
      } catch (err) {
        toast.error(err.response?.data?.error ?? "Could not add member");
      } finally {
        setMemberSaving(false);
      }
    } else {
      // Create mode — stage locally
      setMembers((prev) => [
        ...prev,
        {
          user_id: addingUserId,
          email: user.email,
          full_name: user.full_name,
          notification_prefs: null,
        },
      ]);
      setAddingUserId("");
    }
  }

  async function handleRemoveMember(userId) {
    if (isEdit) {
      try {
        await removeChannelMember(channel.id, userId);
        setMembers((prev) => prev.filter((m) => (m.user_id ?? m.id) !== userId));
        toast.success(t("panel.success_remove"));
      } catch (err) {
        toast.error(err.response?.data?.error ?? "Could not remove member");
      }
    } else {
      setMembers((prev) => prev.filter((m) => (m.user_id ?? m.id) !== userId));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await updateChannel(channel.id, {
          name: name.trim(),
          description: description.trim() || null,
        });
        // Members and endpoints were already synced live — pass through
        saved.members = members;
        saved.endpoints = endpoints;
      } else {
        saved = await createChannel({
          name: name.trim(),
          description: description.trim() || null,
        });

        // Batch-add staged members
        for (const m of members) {
          try {
            await addChannelMember(saved.id, m.user_id ?? m.id);
          } catch {
            // best-effort
          }
        }

        // Batch-add staged endpoints
        for (const ep of endpoints) {
          try {
            await addChannelEndpoint(saved.id, { type: ep.type, config: ep.config });
          } catch {
            // best-effort
          }
        }

        saved.member_count = members.length;
        saved.members = members;
        saved.endpoints = endpoints;
      }
      onSaved(saved);
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not save channel");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="fixed inset-y-0 right-0 m-0 flex h-full w-full max-lg flex-col rounded-none bg-white p-0 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {isEdit ? t("panel.title_edit") : t("panel.title_new")}
            </h2>
            {isEdit && <p className="mt-0.5 text-xs text-gray-400">{channel.name}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {/* Name */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("panel.name")}
              </label>
              <TextInput
                required
                autoFocus
                value={name}
                onValueChange={setName}
                placeholder={t("panel.name_placeholder")}
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                {t("panel.description")}{" "}
                <span className="font-normal text-gray-400">{t("panel.optional")}</span>
              </label>
              <TextInput
                value={description}
                onValueChange={setDescription}
                placeholder={t("panel.description_placeholder")}
              />
            </div>

            {/* Members */}
            <div>
              <label className="mb-2 block text-xs font-medium text-gray-600">
                {t("panel.members_title")}
              </label>

              {loadingMembers ? (
                <p className="text-sm text-gray-400">{t("panel.loading_members")}</p>
              ) : (
                <>
                  {/* Add member picker */}
                  {nonMembers.length > 0 && (
                    <div className="mb-3 flex gap-2">
                      <select
                        value={addingUserId}
                        onChange={(e) => setAddingUserId(e.target.value)}
                        className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600"
                      >
                        <option value="">{t("panel.add_member_placeholder")}</option>
                        {nonMembers.map((m) => (
                          <option key={m.user_id ?? m.id} value={m.user_id ?? m.id}>
                            {m.full_name ? `${m.full_name} (${m.email})` : m.email}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!addingUserId || memberSaving}
                        onClick={handleAddMember}
                      >
                        {t("panel.add_button")}
                      </Button>
                    </div>
                  )}

                  {/* Member list */}
                  {members.length === 0 ? (
                    <p className="text-sm text-gray-400">{t("panel.no_members")}</p>
                  ) : (
                    <div className="divide-y divide-gray-100 rounded-md border border-gray-200">
                      {members.map((m) => (
                        <div
                          key={m.user_id ?? m.id}
                          className="flex items-center justify-between px-4 py-3"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {m.full_name || m.email}
                            </p>
                            {m.full_name && (
                              <p className="text-xs text-gray-400">{m.email}</p>
                            )}
                            <p className="mt-0.5 text-xs text-gray-400">
                              {prefSummary(m.notification_prefs, t)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveMember(m.user_id ?? m.id)}
                            className="rounded p-1 text-gray-400 hover:text-red-500"
                            title={t("panel.success_remove")}
                          >
                            <UserMinus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Endpoints — available always */}
            <EndpointsSection
              channelId={channel?.id}
              endpoints={endpoints}
              onEndpointAdded={(ep) => setEndpoints((prev) => [...prev, ep])}
              onEndpointRemoved={(id) =>
                setEndpoints((prev) => prev.filter((ep) => ep.id !== id))
              }
            />

            {isEdit && <DeliveryLogSection channelId={channel.id} />}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <Button variant="secondary" type="button" onClick={onClose}>
              {t("common:common.cancel")}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving
                ? t("panel.saving")
                : isEdit
                  ? t("panel.save_changes")
                  : t("panel.create_group")}
            </Button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Channels() {
  const { t } = useTranslation(["channels", "common"]);
  const [panelChannel, setPanelChannel] = useState(null); // null = closed, "new" = create, channel obj = edit
  const qc = useQueryClient();

  const channelsQuery = useChannels();
  const membersQuery = useMembers();
  const channels = channelsQuery.data ?? [];
  const allMembers = membersQuery.data ?? [];
  const loading = channelsQuery.isPending || membersQuery.isPending;

  const invalidateChannels = () =>
    qc.invalidateQueries({ queryKey: keys.channels.all });

  function handleSaved(saved) {
    const existed = channels.find((c) => c.id === saved.id);
    invalidateChannels();
    setPanelChannel(null);
    toast.success(existed ? t("table.success_save") : t("table.success_create"));
  }

  async function handleDelete(channel) {
    if (!confirm(t("table.delete_confirm", { name: channel.name }))) return;
    try {
      await deleteChannel(channel.id);
      invalidateChannels();
      if (panelChannel?.id === channel.id) setPanelChannel(null);
      toast.success(t("table.success_delete"));
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Could not delete channel");
    }
  }

  async function handleTest(channel) {
    try {
      const result = await testChannel(channel.id);
      if (result.sent === 0) {
        toast.info(result.message || "No endpoints configured — add an endpoint first.");
      } else {
        toast.success(`Test sent to ${result.sent} endpoint${result.sent > 1 ? "s" : ""}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error ?? "Test failed");
    }
  }

  const panelOpen = panelChannel !== null;
  const editChannel = panelChannel === "new" ? null : panelChannel;

  return (
    <>
      {panelOpen && (
        <ChannelPanel
          channel={editChannel}
          allMembers={allMembers}
          onClose={() => setPanelChannel(null)}
          onSaved={handleSaved}
        />
      )}

      <div className="space-y-6">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
            <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
          </div>
          <Button icon={Plus} onClick={() => setPanelChannel("new")}>
            {t("new_group")}
          </Button>
        </div>

        <Card className="p-0">
          {loading ? (
            <p className="px-5 py-4 text-sm text-gray-400">{t("loading")}</p>
          ) : channels.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">{t("empty.title")}</p>
              <p className="mt-1 text-xs text-gray-400">{t("empty.description")}</p>
            </div>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("table.name")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.description")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.members")}</TableHeaderCell>
                  <TableHeaderCell>{t("table.monitors")}</TableHeaderCell>
                  <TableHeaderCell></TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {channels.map((c) => (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => setPanelChannel(c)}
                  >
                    <TableCell className="font-medium text-gray-900">
                      {c.name}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-gray-500">
                      {c.description || <span className="text-gray-300">—</span>}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {c.member_count ?? 0}
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {c.monitor_count ?? 0}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleTest(c)}
                          className="rounded px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 hover:text-teal-800"
                          title="Send a test notification to all endpoints"
                        >
                          Test
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(c)}
                          className="rounded p-1.5 text-gray-400 hover:text-red-500"
                          title={t("common:common.delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
